import { useState, useEffect, useId } from "react";
import useApi from "@/hooks/useApi.ts";
import {
  UserState,
  ApiAnswer,
  ConnectionCreate,
  ConnectionUserUpdate,
  ConnectionUser,
} from "@/types/ControlPanel";
import { AuthUser } from "@/types/Auth.ts";
import { useLocation } from "react-router-dom";
import { useAppContext } from "@/context/app";

interface UseControlPanelHook {
  token?: string;
  logedUser: AuthUser | null;
  getConnections: () => Promise<ConnectionUser[]>;
  registerConnection: (data: ConnectionCreate) => Promise<void>;
  updateConnection: (data: ConnectionUserUpdate) => Promise<void>;
  updateUserState: (
    user: AuthUser | null,
    state: UserState,
    email?: string
  ) => void;
  updateUserChecker: (state?: UserState, companyName?: string | null) => void;
}

const useControlPanel = (): UseControlPanelHook => {
  const { auth: { user }, controlPanelUser, setControlPanelUser } = useAppContext();
  const idGenerated = useId();
  const location = useLocation();
  const { token, apiGet, apiPost, apiPut } = useApi();
  const [logedUser, setLogedUser] = useState<AuthUser | null>(null);
  const [locationKey, setLocationKey] = useState<string | null>(null);

  useEffect(() => {
    if (locationKey && location.key !== locationKey) {
      updateUserChecker(UserState.INACTIVE, null);
    }
  }, [location]);

  const getConnections = (): Promise<ConnectionUser[]> => {
    return apiGet<ConnectionUser[]>(`/cconnections/admin`);
  };

  const registerConnection = (params: ConnectionCreate): Promise<void> => {
    return apiPost<ConnectionCreate, void>(`/cconnections/admin/`, params);
  };

  const updateConnection = (params: ConnectionUserUpdate): Promise<void> => {
    return apiPut<ConnectionUserUpdate, ApiAnswer>(
      `/cconnections/admin`,
      params
    ).then((r: ApiAnswer) => {
      if (!r.success && user?.email) {
        registerConnection({
          userId: user?.email,
        });
      }
    });
  };

  const updateUserState = (
    user: AuthUser | null,
    state: UserState,
    email?: string
  ) => {
    if (user && !logedUser) {
      setLogedUser(user);
      updateConnection({
        userId: user?.email,
        state: state,
      });
    } else if (logedUser && !user) {
      updateConnection({
        userId: logedUser?.email,
        state: state,
      });
      setLogedUser(null);
    } else if (email) {
      updateConnection({
        userId: email,
        state: state,
      });
    }
    setControlPanelUser({
      state: state,
    });
  };

  const updateUserChecker = (
    state?: UserState,
    companyName?: string | null
  ) => {
    if (user && state === UserState.ACTIVE && companyName) {
      const idChecking = idGenerated;
      setControlPanelUser({
        userInCompany: {
          email: user.email,
          companyName: companyName,
          idChecking: idChecking,
        },
        state: UserState.ACTIVE,
      });
      const temp = {
        id: idChecking,
        companyName: companyName,
      };
      setLocationKey(location.key);
      updateConnection({ userId: user.email, checking: temp });
    } else if (controlPanelUser.userInCompany) {
      updateConnection({
        userId: controlPanelUser.userInCompany.email,
        checking: {
          id: controlPanelUser.userInCompany.idChecking,
          companyName: controlPanelUser.userInCompany.companyName,
        },
      });
      setLocationKey(null);
    }
  };

  return {
    token,
    logedUser,
    getConnections,
    updateUserState,
    updateConnection,
    updateUserChecker,
    registerConnection,
  };
};

export default useControlPanel;
